/**
 * WorkflowOrganizationService
 *
 * Organizes flat timeline_items into hierarchical workflow structure with
 * contextual state management and progressive disclosure logic.
 *
 * Implements the workflow philosophy from WORKFLOWS-AND-TASK-PhiLOSOPHY.md:
 * - Level 1: Workflows (Complete Projects)
 * - Level 2: Sub-Workflows (Major Phases)
 * - Level 3: Steps (Individual Actions)
 *
 * Contextual States:
 * - NOT_STARTED: Collapsed summary
 * - ACTIVE_WITH_PREREQUISITE: Shows prerequisite + locked items
 * - ACTIVE_IN_PROGRESS: Shows current + context
 * - NEARLY_COMPLETE: Focus on finish
 */

const WORKFLOW_DEFINITIONS = {
  build_strategic_profile: {
    title: 'Build Your Strategic Profile',
    description: 'Establish your foundation: who you are, what you offer, and what you\'re capable of',
    icon: '👤',
    category: 'profile',
    order: 1
  },
  build_college_list: {
    title: 'Build Your College List',
    description: 'Research, compare, and finalize your target schools',
    icon: '🎓',
    category: 'research',
    order: 2
  },
  secure_recommendations: {
    title: 'Secure Strong Recommendations',
    description: 'Get teachers and counselors to advocate for you',
    icon: '✉️',
    category: 'application',
    order: 3  // Moved before applications (was after)
  },
  write_personal_statement: {
    title: 'Write Your Personal Statement',
    description: 'Craft your Common App personal essay',
    icon: '✍️',
    category: 'essay',
    order: 4  // Moved before applications (was after)
  },
  setup_application_accounts: {
    title: 'Set Up Application Accounts',
    description: 'Get your application infrastructure ready',
    icon: '🔐',
    category: 'application',
    order: 5  // Moved before applications (was after)
  },
  prepare_for_testing: {
    title: 'Prepare for Testing',
    description: 'Take SAT/ACT and submit scores',
    icon: '📝',
    category: 'testing',
    order: 6  // Added (was missing from definitions)
  },
  research_scholarships: {
    title: 'Research and Pursue Scholarships',
    description: 'Find and apply for scholarship opportunities',
    icon: '💰',
    category: 'financial_aid',
    order: 7  // Updated from 6
  },
  // complete_[college]_app workflows are dynamically generated - order: 8
  respond_to_waitlist: {
    title: 'Respond to Waitlist',
    description: 'Create waitlist strategy and submit updates',
    icon: '⏳',
    category: 'application',
    order: 9  // Kept at 9
  },
  compare_financial_aid: {
    title: 'Compare Financial Aid Offers',
    description: 'Review and compare aid packages',
    icon: '📊',
    category: 'financial_aid',
    order: 10  // Moved to 2nd to last (was 8)
  },
  prepare_for_enrollment: {
    title: 'Prepare for Enrollment',
    description: 'Complete enrollment requirements and get ready for college',
    icon: '🎉',
    category: 'application',
    order: 11  // Updated from 10 (LAST workflow)
  }
};

class WorkflowOrganizationService {
  /**
   * Organize timeline items into hierarchical workflow structure
   * @param {Array} timelineItems - Flat array of timeline_items from database
   * @returns {Array} Array of organized workflows with sub-workflows and steps
   */
  static organizeIntoWorkflows(timelineItems) {
    // Filter out items without workflow_key (not part of workflow system)
    const workflowItems = timelineItems.filter(item => item.workflow_key);

    // Group by workflow_key
    const workflowGroups = this.groupBy(workflowItems, 'workflow_key');

    // Build workflow structure
    const workflows = Object.keys(workflowGroups).map(workflowKey => {
      const items = workflowGroups[workflowKey];
      const definition = WORKFLOW_DEFINITIONS[workflowKey] || this.inferWorkflowDefinition(workflowKey, items);

      return this.buildWorkflow(workflowKey, definition, items);
    });

    // Sort by order
    return workflows.sort((a, b) => a.order - b.order);
  }

  /**
   * Build a complete workflow object with sub-workflows and steps
   * @private
   */
  static buildWorkflow(workflowKey, definition, items) {
    // Group items by sub-workflow
    const subWorkflowGroups = this.groupBy(items, 'sub_workflow_key');

    // Build sub-workflows
    const subWorkflows = Object.keys(subWorkflowGroups)
      .filter(key => key !== 'null' && key !== 'undefined')
      .map(subWorkflowKey => {
        const subItems = subWorkflowGroups[subWorkflowKey];
        return this.buildSubWorkflow(subWorkflowKey, subItems);
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);

    // Calculate workflow progress
    const totalSteps = items.filter(item => item.hierarchy_level === 3).length;
    const completedSteps = items.filter(item =>
      item.hierarchy_level === 3 && item.status === 'completed'
    ).length;
    const skippedSteps = items.filter(item =>
      item.hierarchy_level === 3 && (item.status === 'skipped' || item.status === 'cancelled')
    ).length;
    const finishedSteps = completedSteps + skippedSteps; // Combined completed + skipped
    const inProgressSteps = items.filter(item =>
      item.hierarchy_level === 3 && item.status === 'in_progress'
    ).length;

    // Determine workflow state (using finishedSteps = completed + skipped)
    const state = this.determineWorkflowState(subWorkflows, finishedSteps, totalSteps);

    // Find next action
    const nextAction = this.findNextAction(subWorkflows);

    return {
      workflowKey,
      ...definition,
      subWorkflows,
      totalSubWorkflows: subWorkflows.length,
      totalSteps,
      completedSteps, // Keep for display purposes
      skippedSteps,
      finishedSteps, // Total of completed + skipped
      inProgressSteps,
      completionPercentage: totalSteps > 0 ? Math.round((finishedSteps / totalSteps) * 100) : 0,
      state,
      nextAction,
      isExpanded: state === 'active_in_progress' || state === 'active_with_prerequisite',
      // Metadata
      metadata: items[0]?.workflow_metadata || {}
    };
  }

  /**
   * Build a sub-workflow object with its steps
   * @private
   */
  static buildSubWorkflow(subWorkflowKey, items) {
    // Sort steps by display_order
    const steps = items
      .filter(item => item.hierarchy_level === 3)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    const completedSteps = steps.filter(s => s.status === 'completed').length;
    const skippedSteps = steps.filter(s => s.status === 'skipped' || s.status === 'cancelled').length;
    const finishedSteps = completedSteps + skippedSteps; // Combined completed + skipped
    const totalSteps = steps.length;

    // Check if this is a prerequisite sub-workflow
    const isPrerequisite = items.some(item =>
      item.is_foundation_task ||
      (item.workflow_metadata?.is_prerequisite === true)
    );

    // Check if locked (any step is locked)
    const isLocked = steps.some(s => s.is_locked);

    // Determine current status (consider both completed and skipped as "finished")
    let status = 'not_started';
    if (finishedSteps === totalSteps && totalSteps > 0) {
      status = 'completed';
    } else if (finishedSteps > 0 || steps.some(s => s.status === 'in_progress')) {
      status = 'in_progress';
    } else if (isLocked) {
      status = 'locked';
    }

    // Find current step (first non-finished, unlocked step)
    const currentStep = steps.find(s =>
      s.status !== 'completed' &&
      s.status !== 'skipped' &&
      s.status !== 'cancelled' &&
      !s.is_locked
    );

    // Get metadata from first item
    const metadata = items[0]?.workflow_metadata || {};

    return {
      subWorkflowKey,
      title: metadata.sub_workflow_title || this.formatTitle(subWorkflowKey),
      description: metadata.description || '',
      steps,
      totalSteps,
      completedSteps,
      skippedSteps,
      finishedSteps, // Combined completed + skipped
      completionPercentage: totalSteps > 0 ? Math.round((finishedSteps / totalSteps) * 100) : 0,
      isPrerequisite,
      isLocked,
      status,
      currentStep,
      displayOrder: items[0]?.display_order || 0,
      unlockDescription: items.find(i => i.unlock_description)?.unlock_description || null,
      contextualTip: metadata.contextual_tip || null,
      estimatedHours: metadata.estimated_hours || null,
      isExpanded: status === 'in_progress', // Expand if actively working
      metadata
    };
  }

  /**
   * Determine the contextual state of a workflow
   * Based on WORKFLOWS-AND-TASK-PhiLOSOPHY.md states
   * @private
   */
  static determineWorkflowState(subWorkflows, finishedSteps, totalSteps) {
    if (finishedSteps === totalSteps && totalSteps > 0) {
      return 'complete';
    }

    const hasPrerequisite = subWorkflows.some(sw => sw.isPrerequisite);
    const prerequisiteComplete = subWorkflows
      .filter(sw => sw.isPrerequisite)
      .every(sw => sw.status === 'completed');

    if (hasPrerequisite && !prerequisiteComplete) {
      return 'active_with_prerequisite';
    }

    if (finishedSteps === 0) {
      return 'not_started';
    }

    const percentComplete = totalSteps > 0 ? (finishedSteps / totalSteps) * 100 : 0;
    if (percentComplete >= 75) {
      return 'nearly_complete';
    }

    return 'active_in_progress';
  }

  /**
   * Find the next actionable step in a workflow
   * @private
   */
  static findNextAction(subWorkflows) {
    // First, check for prerequisite sub-workflows
    const prerequisite = subWorkflows.find(sw =>
      sw.isPrerequisite && sw.status !== 'completed'
    );
    if (prerequisite && prerequisite.currentStep) {
      return {
        title: prerequisite.currentStep.title,
        subWorkflow: prerequisite.title,
        context: `Prerequisite • Unlocks ${prerequisite.unlockDescription || 'dependent tasks'}`,
        step: prerequisite.currentStep
      };
    }

    // Then, find first in-progress or available sub-workflow
    for (const subWorkflow of subWorkflows) {
      if (subWorkflow.status === 'locked') continue;
      if (subWorkflow.currentStep) {
        return {
          title: subWorkflow.currentStep.title,
          subWorkflow: subWorkflow.title,
          context: subWorkflow.contextualTip || '',
          step: subWorkflow.currentStep
        };
      }
    }

    return null;
  }

  /**
   * Infer workflow definition from items if not in WORKFLOW_DEFINITIONS
   * (for dynamically generated workflows like complete_stanford_app)
   * @private
   */
  static inferWorkflowDefinition(workflowKey, items) {
    const metadata = items.find(item => item.workflow_metadata?.title)?.workflow_metadata;

    // Check if this is a college-specific workflow
    const isCollegeWorkflow = workflowKey.startsWith('complete_') && workflowKey.endsWith('_app');

    if (isCollegeWorkflow) {
      const collegeName = items[0]?.college_name || 'College';
      return {
        title: `Complete ${collegeName} Application`,
        description: `All tasks for ${collegeName} application`,
        icon: '🎓',
        category: 'application',
        order: 8, // College workflows come after prep workflows (recommendations, essays, accounts, testing, scholarships)
        isDynamic: true,
        collegeName
      };
    }

    // Fallback for unknown workflows
    return {
      title: metadata?.title || this.formatTitle(workflowKey),
      description: metadata?.description || '',
      icon: '📋',
      category: items[0]?.category || 'application',
      order: 99
    };
  }

  /**
   * Calculate recovery mode metrics
   * Determines if user needs recovery banner
   */
  static calculateRecoveryMetrics(workflows) {
    const allSteps = workflows.flatMap(w =>
      w.subWorkflows.flatMap(sw => sw.steps)
    );

    const now = new Date();
    const overdueTasks = allSteps.filter(step => {
      // Exclude completed, skipped, and cancelled tasks
      if (step.status === 'completed' || step.status === 'skipped' || step.status === 'cancelled') return false;
      const dueDate = new Date(step.due_date || step.scheduled_date);
      return dueDate < now;
    });

    const upcomingTwoWeeks = allSteps.filter(step => {
      // Exclude completed, skipped, and cancelled tasks
      if (step.status === 'completed' || step.status === 'skipped' || step.status === 'cancelled') return false;
      const dueDate = new Date(step.due_date || step.scheduled_date);
      const twoWeeksFromNow = new Date();
      twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
      return dueDate <= twoWeeksFromNow && dueDate >= now;
    });

    const totalHoursNext2Weeks = upcomingTwoWeeks.reduce((sum, step) => {
      const hours = step.workflow_metadata?.estimated_minutes
        ? step.workflow_metadata.estimated_minutes / 60
        : (step.estimated_hours || 1);
      return sum + hours;
    }, 0);

    return {
      overdueCount: overdueTasks.length,
      upcomingTwoWeeksCount: upcomingTwoWeeks.length,
      totalHoursNext2Weeks: Math.round(totalHoursNext2Weeks),
      isInRecoveryMode: overdueTasks.length >= 5 || totalHoursNext2Weeks >= 20,
      mostOverdueTask: overdueTasks.length > 0
        ? overdueTasks.sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0]
        : null
    };
  }

  /**
   * Get summary statistics for timeline dashboard
   */
  static getSummaryStats(workflows) {
    const activeWorkflows = workflows.filter(w =>
      w.state === 'active_in_progress' || w.state === 'active_with_prerequisite'
    );

    const totalSteps = workflows.reduce((sum, w) => sum + w.totalSteps, 0);
    const completedSteps = workflows.reduce((sum, w) => sum + w.completedSteps, 0);
    const skippedSteps = workflows.reduce((sum, w) => sum + w.skippedSteps, 0);
    const finishedSteps = completedSteps + skippedSteps;

    // Find next unlocking step
    const nextUnlockingStep = workflows
      .flatMap(w => w.subWorkflows)
      .find(sw => sw.isPrerequisite && sw.status !== 'completed');

    return {
      activeWorkflows: activeWorkflows.length,
      totalSteps,
      completedSteps,
      skippedSteps,
      finishedSteps,
      completionPercentage: totalSteps > 0 ? Math.round((finishedSteps / totalSteps) * 100) : 0,
      nextUnlockingStep: nextUnlockingStep ? {
        title: nextUnlockingStep.title,
        unlocks: nextUnlockingStep.unlockDescription || `${nextUnlockingStep.steps.length} tasks`
      } : null
    };
  }

  /**
   * Utility: Group array by key
   * @private
   */
  static groupBy(array, key) {
    return array.reduce((result, item) => {
      const groupKey = item[key] || 'ungrouped';
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(item);
      return result;
    }, {});
  }

  /**
   * Utility: Format workflow/sub-workflow key to title
   * @private
   */
  static formatTitle(key) {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}

export default WorkflowOrganizationService;
